/**
 * Fire-and-forget client error reporting to Sparkle's `POST /report_error`
 * endpoint (ErrorsController#report), which forwards the report to Honeybadger
 * as a "JS Error Report" fault. Nothing else forwards frontend errors — plain
 * `console.error` is never reported.
 *
 * Honeybadger groups reports into faults by the first line of `message`, so
 * keep `message` a stable string and put the variable detail (request URL,
 * error text) in `description`.
 *
 * The page URL is appended to `description`, which matters most on embedded
 * host pages where the report is sent to the Sparkle origin (via `baseUrl`)
 * rather than the page the user is actually on.
 *
 * @param {String} message - stable, human-readable summary of the failure
 * @param {Object} [options]
 * @param {String} [options.description] - variable detail: failing URL, error message, etc.
 * @param {String} [options.baseUrl] - URL whose origin the report should be sent to;
 *   defaults to the page origin. Resolved against the page, so an absolute request URL
 *   targets the Sparkle origin while a relative one falls back to the page. Pass this
 *   when the component can run embedded on a non-Sparkle host page.
 * @param {Boolean} [options.omitPage] - leave the page's URL out of the
 *   description entirely. For reporters that can fire on any page of a
 *   healthcare site: query strings can carry PHI and the path itself can be a
 *   bearer capability (appointment-token routes), so no portion of the
 *   address is safe to ship to the error tracker.
 */
export function reportClientError(message, { description, baseUrl, omitPage = false } = {}) {
  const pageUrl = window.location.href;
  const fullDescription = [description, omitPage ? null : `page: ${pageUrl}`].filter(Boolean).join(" — ");

  console.error(message, fullDescription);

  try {
    // Resolve baseUrl (absolute or relative) against the page, then target
    // /report_error on that origin — an absolute Sparkle request URL wins, a
    // relative one falls back to the page origin.
    const origin = new URL(baseUrl ?? pageUrl, pageUrl);
    const endpoint = new URL("/report_error", origin);
    // URLSearchParams posts as application/x-www-form-urlencoded — a CORS
    // "simple request", so embedded (cross-origin) pages report without a preflight.
    const body = new URLSearchParams({
      "report[message]": message,
      "report[description]": fullDescription,
    });

    // no-referrer: the default policy attaches the full page URL as the
    // Referer header on same-origin posts, and the server forwards request
    // headers to the error tracker — the same leak the description field
    // avoids. No caller needs a referer here (legacy callers carry the page
    // in the description deliberately).
    fetch(endpoint, { method: "POST", body, keepalive: true, referrerPolicy: "no-referrer" }).catch(() => {});
  } catch {
    // Reporting must never break the caller.
  }
}
